import { ProductType } from 'types/chart'
import styles from './PriceHistoryElement.module.scss'
import PriceHistoryChart from './PriceHistoryChart'
import { useState, useMemo } from 'react'
import { PriceHistoryElementProps } from './types'
import { Button, Card, Icon, LoadingSkeleton, Select, Toggle } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'

const PriceHistoryElement = ({ priceHistoryData, currentDateString, minStartDateString }: PriceHistoryElementProps) => {
  // Local constants
  //convert strings back to date
  const minStartDate = new Date(minStartDateString)
  const currentDate = new Date(currentDateString)

  // i18n
  const { t } = useTranslation('common')

  // Local state
  const [activeProduct, setActiveProduct] = useState<ProductType>(ProductType.ELECTRICITY)
  const [period, setPeriod] = useState(currentDate.getFullYear())

  // Memoized constants
  const years = useMemo<number[]>(() => {
    const years: number[] = []
    for (let year = minStartDate.getFullYear(); year <= currentDate.getFullYear(); year++) {
      years.push(year)
    }
    return years
  }, [])

  /**
   * Switch active product type
   */
  const switchActiveType = () => {
    setActiveProduct((prev) => (prev === ProductType.ELECTRICITY ? ProductType.GAS : ProductType.ELECTRICITY))
  }

  /**
   * Switch active period by + or - 1 year
   * @param {'prev' | 'next'} change
   */
  const switchActivePeriod = (change: 'prev' | 'next') => {
    if (change === 'prev') {
      setPeriod((prev) => prev - 1)
    } else if (change === 'next') {
      setPeriod((prev) => prev + 1)
    }
  }

  /**
   * Select active period from dropdown
   * @param {number} period - selected period
   */
  const selectActivePeriod = (period: number) => {
    setPeriod(period)
  }

  return (
    <Card className={styles.card} color="white" border>
      {/* NAVIGATION */}
      <div className={styles['control-elements']}>
        {/* PERIOD SELECTION */}
        <div className={styles['period-selection']}>
          <Button
            type="button"
            onClick={() => switchActivePeriod('prev')}
            className={styles['period-btn']}
            disabled={period <= minStartDate.getFullYear()}
            leadingIcon="chevronLeft"
            size="small"
            variant="tertiary"
          />

          <Select
            label="Period"
            hiddenLabel
            name="period"
            aria-label="period"
            value={period}
            className={styles['period-dropdown']}
            onChange={(e) => selectActivePeriod(Number(e.target.value))}
          >
            {years.map((year) => (
              <option value={year} key={year}>
                {year}
              </option>
            ))}
          </Select>

          <Button
            type="button"
            onClick={() => switchActivePeriod('next')}
            className={styles['period-btn']}
            disabled={period >= new Date().getFullYear()}
            leadingIcon="chevronRight"
            size="small"
            variant="tertiary"
          />
        </div>

        {/* PRODUCT SELECTION */}
        <Toggle
          active={activeProduct}
          onClick={switchActiveType}
          className={styles.toggle}
          options={[
            { value: ProductType.ELECTRICITY, label: <Icon name="electricity" size={20} aria-label={t('electricity')} /> },
            { value: ProductType.GAS, label: <Icon name="gas" size={20} aria-label={t('gas')} /> }
          ]}
        />
      </div>

      {/* CHART */}
      {priceHistoryData?.[activeProduct] ? (
        <PriceHistoryChart data={priceHistoryData[activeProduct]} activeProduct={activeProduct} year={period} />
      ) : (
        <LoadingSkeleton>
          <LoadingSkeleton.Rectangle aspectRatio="4 / 1" />
          <LoadingSkeleton.Rectangle height={30} />
        </LoadingSkeleton>
      )}
    </Card>
  )
}

export default PriceHistoryElement
